import React, { useEffect, useState } from 'react';

import { type IMutableTableInternalState } from '../../components/IMutableTableInternalState';
import { type ITableBaseCellContext } from '../../components/ITableBaseCellContext';
import { type TPandasDataFrameDict } from '../../components/TPandasDataFrameDict';
import { type TScaffoldTableColumnConfig } from '../../components/TScaffoldTableColumnConfig';
import { updateStateAttribute } from '../../helpers/helper';
import { type IBrowsableTableInternalState } from '../../models/IBrowsableTableInternalState';
import CBrowsableTable, { type IBrowsableTableProps } from './CBrowsableTable';
import { HTableAlterations } from './HTableAlterations';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import { useAppDispatch } from '../../hooks/hooks';
import { updateSnackbar } from '../../store/appSlice';

export interface IMutableTableProps extends Omit<IBrowsableTableProps, 'onUpdateInternalState'> {
  // Currently, the tables don't support index rendering as it introduces a whole host of problems.
  onSave?: (data: TPandasDataFrameDict, modifiedRows: TPandasDataFrameDict) => void;
  onUpdateInternalState: (internalState: IMutableTableInternalState) => void;
  errors?: Record<string, string[]>;
}

interface State {
  modifiedTable: TPandasDataFrameDict;
  rowNamesDeletionList: string[];
  rowNamesSelectionList: string[];
  browsableTableState?: IBrowsableTableInternalState;
}

/**
 * Implementation Notes: The internal table is replaced by the prop data each time it changes -- i.e., changes are not
 * preserved when the prop data changes. As such, it is discouraged to store table modifications externally.
 * @param props
 * @constructor
 */
export default function CMutableTable(props: IMutableTableProps) {
  const [state, setState] = useState<State>({
    modifiedTable: {},
    rowNamesDeletionList: [],
    rowNamesSelectionList: []
  });
  const dispatch = useAppDispatch();

  const unsavedChangesMessage = CLocalizedTextStringStatic('mutableTable.unsavedChangesBlocked');

  const originalColumns = Object.keys(props.data ?? {});
  const originalRowNames = Array.from(
    new Set(originalColumns.flatMap((col) => Object.keys(props.data?.[col] ?? {}))).values()
  );

  const handleCellChange = (context: Omit<ITableBaseCellContext, 'rowValues'>) => {
    HTableAlterations.replaceCell(state.modifiedTable, context, (modifiedTable) => {
      updateStateAttribute<State>({ modifiedTable }, setState);
    });
  };

  const handleAppendRow = () => {
    HTableAlterations.appendRow(state.modifiedTable, undefined, (modifiedTable) => {
      updateStateAttribute<State>({ modifiedTable }, setState);
    });
  };

  const handleSave = () => {
    const allRows = HTableAlterations.deleteRows(state.modifiedTable, state.rowNamesDeletionList, (modifiedTable) => {
      updateStateAttribute<State>({ modifiedTable, rowNamesDeletionList: [] }, setState);
    });
    const modifiedTableRowNames = Array.from(
      new Set(originalColumns.flatMap((col) => Object.keys(state.modifiedTable?.[col] ?? {}))).values()
    );
    const unmodifiedRows = modifiedTableRowNames.filter((e) => !isRowModified(e));
    const modifiedRowsOnly = HTableAlterations.deleteRows(state.modifiedTable, unmodifiedRows, () => {});
    props.onSave?.(allRows, modifiedRowsOnly);
  };

  const isRowModified = (rowName: string) => {
    return (
      !(
        !originalRowNames.includes(rowName) ||
        JSON.stringify(HTableAlterations.extractRow(props.data ?? {}, rowName)) ===
          JSON.stringify(HTableAlterations.extractRow(state.modifiedTable, rowName))
      ) ||
      (state.rowNamesDeletionList.includes(rowName) && originalRowNames.includes(rowName))
    );
  };

  const isRowMarkedForDeletion = (rowName: string) => {
    return state.rowNamesDeletionList.includes(rowName);
  };

  const toggleRowSelection = (rowName: string) => {
    if (!state.rowNamesSelectionList.includes(rowName)) {
      updateStateAttribute<State>({ rowNamesSelectionList: [...state.rowNamesSelectionList, rowName] }, setState);
    } else {
      updateStateAttribute<State>(
        { rowNamesSelectionList: state.rowNamesSelectionList.filter((e) => e !== rowName) },
        setState
      );
    }
  };

  const toggleRowNameFromDeleteList = (rowName: string) => {
    if (!state.rowNamesDeletionList.includes(rowName)) {
      updateStateAttribute<State>({ rowNamesDeletionList: [...state.rowNamesDeletionList, rowName] }, setState);
    } else {
      updateStateAttribute<State>(
        { rowNamesDeletionList: state.rowNamesDeletionList.filter((e) => e !== rowName) },
        setState
      );
    }
  };

  const toggleRowDeletion = (context: ITableBaseCellContext) => {
    // context.rowValues.filter((e) => e !== null).length === 0
    //   ? HTableAlterations.deleteRows(state.modifiedTable, [context.rowName], (modifiedTable) => {
    //       updateStateAttribute<State>({ modifiedTable }, setState);
    //     })
    //   : toggleRowNameFromDeleteList(context.rowName);
    toggleRowNameFromDeleteList(context.rowName);
  };

  const handleRevertRowChange = (rowName: string) => {
    HTableAlterations.replaceRow(
      state.modifiedTable,
      rowName,
      HTableAlterations.extractRow(props.data ?? {}, rowName),
      (modifiedTable) => {
        updateStateAttribute<State>({ modifiedTable }, setState);
      }
    );
    state.rowNamesDeletionList.includes(rowName) && toggleRowNameFromDeleteList(rowName);
  };

  const handleRevertAllChanges = () => {
    const columns = Object.keys(props.data ?? {});
    const rowNames = Array.from(new Set(columns.flatMap((col) => Object.keys(props.data?.[col] ?? {}))).values());

    const utilDict: TPandasDataFrameDict[''] = {};

    for (const rowName of rowNames) {
      utilDict[rowName] = rowName;
    }

    updateStateAttribute<State>(
      {
        modifiedTable: JSON.parse(JSON.stringify(props.data ?? {})),
        rowNamesDeletionList: [],
        rowNamesSelectionList: []
      },
      setState
    );
  };

  const backgroundColorStyleMods = (rowName: string) => {
    const style: Partial<React.CSSProperties> = {};
    if (state.rowNamesSelectionList.includes(rowName)) {
      style.backgroundColor = 'rgba(191, 219, 254, 0.5)';
    }
    if (state.rowNamesDeletionList.includes(rowName)) {
      style.backgroundColor = style.backgroundColor ? 'rgba(109, 40, 217, 0.1)' : 'rgba(185, 28, 28, 0.15)';
    }
    return style;
  };

  const isTableModified =
    JSON.stringify(state.modifiedTable) !== JSON.stringify(props.data) || state.rowNamesDeletionList.length > 0;

  const handleUpdateTableMeta: IBrowsableTableProps['onUpdateTableMeta'] = (meta) => {
    if (isTableModified) {
      dispatch(
        updateSnackbar({
          message: unsavedChangesMessage,
          severity: 'error'
        })
      );
      return;
    }

    props.onUpdateTableMeta?.(meta);
  };

  useEffect(() => {
    handleRevertAllChanges(); // Reset modified table everytime parent changes -- i.e. edits are not preserved!
  }, [props.data]);

  useEffect(() => {
    const modifiedTableRowNames =
      Array.from(new Set(originalColumns.flatMap((col) => Object.keys(state.modifiedTable[col] ?? {}))).values()) ?? [];
    props.onUpdateInternalState({
      save: {
        disabled: !isTableModified,
        click: handleSave
      },
      revertAll: {
        disabled: !isTableModified,
        click: handleRevertAllChanges
      },
      addRow: {
        click: handleAppendRow
      },
      revertRow: {
        enabled: isRowModified,
        click: handleRevertRowChange
      },
      deleteRow: {
        toggled: isRowMarkedForDeletion,
        click: toggleRowDeletion
      },
      selectRow: {
        selected: (rowName) => state.rowNamesSelectionList.includes(rowName),
        click: toggleRowSelection
      },
      selectAllRow: {
        isTrue: modifiedTableRowNames.filter((e) => !state.rowNamesSelectionList.includes(e)).length === 0,
        click: () => {
          updateStateAttribute<State>(
            {
              rowNamesSelectionList:
                modifiedTableRowNames.filter((e) => !state.rowNamesSelectionList.includes(e)).length === 0
                  ? []
                  : modifiedTableRowNames
            },
            setState
          );
        }
      },
      internalTable: state.modifiedTable,
      selectedRowNames: state.rowNamesSelectionList,
      paginator: state.browsableTableState?.paginator ?? <></>,
      timestamp: (t) => state.browsableTableState?.timestamp(t),
      searchBar: state.browsableTableState?.searchBar
    });
  }, [state.modifiedTable, state.rowNamesDeletionList, state.rowNamesSelectionList, state.browsableTableState]);

  return (
    <div className={'flex flex-col gap-2'}>
      <CBrowsableTable
        {...props}
        data={state.modifiedTable ?? {}}
        onUpdateTableMeta={handleUpdateTableMeta}
        onLocaleChange={(meta) => {
          if (isTableModified) {
            dispatch(
              updateSnackbar({
                message: CLocalizedTextStringStatic('mutableTable.localeReloadBlocked'),
                severity: 'info'
              })
            );
            return;
          }

          props.onLocaleChange?.(meta);
        }}
        rowConfig={{
          ...props.rowConfig,
          body: {
            ...props.rowConfig.body
            // style: (context) => {
            //   return {
            //     ...props.rowConfig.body?.style?.(context)
            //   };
            // }
          }
        }}
        columnConfig={[
          /* Basically, we are just weaving in the strike-through option */
          ...Array(Object.keys(props.data ?? {}).length)
            .fill({ undefined })
            .map((_, ix) => {
              const cfg: TScaffoldTableColumnConfig = props.columnConfig[ix] ?? {};
              return {
                ...cfg,
                body: {
                  ...cfg.body,
                  style: (context) => {
                    return {
                      textDecoration: state.rowNamesDeletionList.includes(context.rowName) ? 'line-through' : 'none',
                      ...backgroundColorStyleMods(context.rowName),
                      ...cfg.body?.style?.(context)
                    };
                  },
                  render: (context) => {
                    const oriValue = HTableAlterations.extractRow(props.data ?? {}, context.rowName)?.[
                      context.cellCoordinate[0]
                    ];
                    const cellCoordinate = context.cellCoordinate.join(',');
                    const errors = cellCoordinate in (props.errors ?? {}) ? props.errors?.[cellCoordinate] : undefined;
                    const cellChanged = context.value !== oriValue && !context.rowName.includes('-');

                    return (
                      <>
                        {cellChanged && (
                          <div
                            className={'absolute h-full w-full border-b-2 border-b-teal -bottom-1'}
                            title={`${oriValue} → ${context.value}`}
                          />
                        )}
                        {!!errors && (
                          <div
                            className={'absolute h-full w-full border-b-2 border-b-red-500 -bottom-1'}
                            title={errors?.join(' ')}
                          />
                        )}
                        {cfg.body?.render?.(context) ?? context.value}
                      </>
                    );
                  }
                }
              } satisfies TScaffoldTableColumnConfig;
            })
        ]}
        edgeColumnConfig={{
          ...(props.edgeColumnConfig?.prefix
            ? {
                prefix: {
                  ...props.edgeColumnConfig?.prefix,
                  body: {
                    ...props.edgeColumnConfig.prefix.body,
                    style: (context) => {
                      return {
                        ...backgroundColorStyleMods(context.rowName),
                        ...props.edgeColumnConfig?.prefix?.body?.style?.(context)
                      };
                    }
                  }
                } satisfies TScaffoldTableColumnConfig
              }
            : {}),
          ...(props.edgeColumnConfig?.suffix
            ? {
                suffix: {
                  ...props.edgeColumnConfig?.suffix,
                  body: {
                    ...props.edgeColumnConfig.suffix.body,
                    render: (context) => <>{props.edgeColumnConfig?.suffix?.body?.render?.(context)}</>,
                    style: (context) => {
                      return {
                        ...backgroundColorStyleMods(context.rowName),
                        ...props.edgeColumnConfig?.suffix?.body?.style?.(context)
                      };
                    }
                  }
                } satisfies TScaffoldTableColumnConfig
              }
            : {})
        }}
        onCellChange={handleCellChange}
        onUpdateInternalState={(internalState) => {
          updateStateAttribute<State>({ browsableTableState: internalState }, setState);
        }}
      />
    </div>
  );
}
